import { Controller, useFormContext } from 'react-hook-form';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import { Modal } from '../../../components/modals/Modal';
import type { ChangeEmailVerifyValues } from '../schemas/changeEmailSchema';

export type VerifyOtpModalProps = {
  open: boolean;
  oldEmail: string;
  newEmail: string;
  loading: boolean;
  secondsRemaining: number;
  resendLoading: boolean;
  resendCooldown: number;
  onSubmit: (values: ChangeEmailVerifyValues) => void;
  onResend: () => void;
  onEditEmail: () => void;
  onCancel: () => void;
};

const otpInputSx = {
  gap: { xs: 0.75, sm: 1.5 },
  '& .MuiOtpInput-TextField': {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: 600,
      height: '52px',
    },
  },
};

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function VerifyOtpModal({
  open,
  oldEmail,
  newEmail,
  loading,
  secondsRemaining,
  resendLoading,
  resendCooldown,
  onSubmit,
  onResend,
  onEditEmail,
  onCancel,
}: VerifyOtpModalProps) {
  const { control, handleSubmit } = useFormContext<ChangeEmailVerifyValues>();

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onCancel}
      sx={{
        '& > .MuiBox-root': {
          width: '100%',
          maxWidth: 460,
          borderRadius: '16px',
        },
      }}
    >
      <Modal.Header>
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography component="h2" sx={{ color: '#fff', fontWeight: 600, fontSize: '18px' }}>
            Verify OTP
          </Typography>
          <IconButton onClick={onCancel} disabled={loading} size="small" sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal.Header>

      <Box component="form" onSubmit={handleSubmit((values) => void onSubmit(values))}>
        <Modal.Body>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Please enter the verification codes sent to your email addresses to authorize this change.
              </Typography>
              {secondsRemaining > 0 && (
                <Typography sx={{ color: 'primary.main', fontWeight: 700, textAlign: 'center' }}>
                  Expires in: {formatCountdown(secondsRemaining)}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                OTP sent to <Box component="span" sx={{ color: 'info.main' }}>{oldEmail}</Box>
              </Typography>
              <Controller
                name="oldEmailCode"
                control={control}
                render={({ field, fieldState }) => (
                  <Box>
                    <MuiOtpInput
                      {...field}
                      length={6}
                      autoFocus
                      sx={otpInputSx}
                      TextFieldsProps={{ error: !!fieldState.error }}
                    />
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
              <Box sx={{ textAlign: 'right' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={onResend}
                  disabled={resendLoading || resendCooldown > 0}
                  sx={{ fontSize: '13px', cursor: resendCooldown > 0 ? 'default' : 'pointer' }}
                >
                  {resendLoading ? 'Resending...' : resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
                </Link>
              </Box>
            </Box>

            <Box sx={{ height: '1px', bgcolor: 'divider' }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  OTP sent to <Box component="span" sx={{ color: 'info.main' }}>{newEmail}</Box>
                </Typography>
                <Tooltip title="Change email">
                  <IconButton onClick={onEditEmail} disabled={loading} size="small">
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Controller
                name="newEmailCode"
                control={control}
                render={({ field, fieldState }) => (
                  <Box>
                    <MuiOtpInput {...field} length={6} sx={otpInputSx} TextFieldsProps={{ error: !!fieldState.error }} />
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
              <Box sx={{ textAlign: 'right' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={onResend}
                  disabled={resendLoading || resendCooldown > 0}
                  sx={{ fontSize: '13px', cursor: resendCooldown > 0 ? 'default' : 'pointer' }}
                >
                  {resendLoading ? 'Resending...' : resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
                </Link>
              </Box>
            </Box>
          </Box>
        </Modal.Body>

        <Modal.Footer>
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              startIcon={<SaveIcon />}
              sx={{
                bgcolor: '#EE5B2B',
                '&:hover': { bgcolor: '#EE5B2B' },
                borderRadius: '20px',
                px: 3,
              }}
            >
              Continue
            </LoadingButton>
          </Box>
        </Modal.Footer>
      </Box>
    </Modal>
  );
}
